import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

const StudentTasks = () => {
  const routes = all_routes;
  const [activeTab, setActiveTab] = useState("pending");

  // Mock data for tasks
  const tasksData = {
    pending: [
      {
        id: 1,
        title: "Math Chapter 5 Problems",
        subject: "Mathematics",
        teacher: "Ms. Johnson",
        type: "homework",
        dueDate: "Tomorrow",
        priority: "high",
        description: "Complete exercises 1-15 from textbook",
      },
      {
        id: 2,
        title: "Physics Lab Report",
        subject: "Physics",
        teacher: "Dr. Smith",
        type: "assignment",
        dueDate: "Aug 12",
        priority: "medium",
        description: "Write lab report on velocity experiment",
      },
      {
        id: 3,
        title: "History Essay",
        subject: "History",
        teacher: "Ms. Wilson",
        type: "project",
        dueDate: "Aug 15",
        priority: "low",
        description: "World War II impact analysis - 1000 words",
      },
      {
        id: 4,
        title: "Chemistry Quiz Preparation",
        subject: "Chemistry",
        teacher: "Dr. Brown",
        type: "exam",
        dueDate: "Aug 10",
        priority: "high",
        description: "Study chapters 3-4 for upcoming quiz",
      },
    ],
    completed: [
      {
        id: 5,
        title: "English Literature Essay",
        subject: "English",
        teacher: "Mr. Davis",
        type: "homework",
        completedDate: "Aug 5",
        grade: "A-",
        description: "Analysis of Shakespeare's Hamlet",
      },
      {
        id: 6,
        title: "Math Test",
        subject: "Mathematics",
        teacher: "Ms. Johnson",
        type: "exam",
        completedDate: "Aug 3",
        grade: "B+",
        description: "Algebra and geometry test",
      },
      {
        id: 7,
        title: "Science Project",
        subject: "Physics",
        teacher: "Dr. Smith",
        type: "project",
        completedDate: "Aug 1",
        grade: "A",
        description: "Solar system model presentation",
      },
    ],
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-danger";
      case "medium":
        return "bg-warning";
      case "low":
        return "bg-info";
      default:
        return "bg-secondary";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "homework":
        return "ti-book";
      case "assignment":
        return "ti-file-text";
      case "project":
        return "ti-briefcase";
      case "exam":
        return "ti-clipboard-list";
      default:
        return "ti-clipboard";
    }
  };

  const renderTaskCard = (task: any, isCompleted = false) => {
    return (
      <div key={task.id} className="card mb-3">
        <div className="card-body">
          <div className="d-flex align-items-start">
            <div
              className={`avatar avatar-sm me-3 ${
                isCompleted ? "bg-success" : getPriorityColor(task.priority)
              }`}
            >
              <i className={`ti ${getTypeIcon(task.type)} text-white`}></i>
            </div>
            <div className="flex-fill">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">{task.title}</h6>
                {isCompleted ? (
                  <span className="badge bg-success">{task.grade}</span>
                ) : (
                  <span className={`badge ${getPriorityColor(task.priority)}`}>
                    {task.priority.charAt(0).toUpperCase() +
                      task.priority.slice(1)}
                  </span>
                )}
              </div>
              <div className="d-flex align-items-center mb-2">
                <span className="badge bg-light text-dark me-2">
                  <i className="ti ti-book me-1"></i>
                  {task.subject}
                </span>
                <span className="badge bg-light text-dark me-2">
                  <i className="ti ti-user me-1"></i>
                  {task.teacher}
                </span>
                <span className="badge bg-light text-dark">
                  <i className="ti ti-calendar me-1"></i>
                  {isCompleted ? task.completedDate : task.dueDate}
                </span>
              </div>
              <p className="text-muted mb-0 small">{task.description}</p>
            </div>
          </div>
          {!isCompleted && (
            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-success btn-sm">
                <i className="ti ti-check me-1"></i>
                Mark Complete
              </button>
              <button className="btn btn-primary btn-sm">
                <i className="ti ti-edit me-1"></i>
                Edit
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.studentDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Tasks</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="card bg-warning">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="text-white">
                      <h3>My Tasks</h3>
                      <p>Manage homework, assignments, projects & exams</p>
                    </div>
                    <div className="avatar avatar-lg">
                      <i className="ti ti-clipboard-list text-white fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row">
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-danger-transparent mb-3">
                    <i className="ti ti-alert-circle fs-2 text-danger"></i>
                  </div>
                  <h4 className="text-danger">
                    {
                      tasksData.pending.filter((t) => t.priority === "high")
                        .length
                    }
                  </h4>
                  <p className="mb-0">High Priority</p>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-warning-transparent mb-3">
                    <i className="ti ti-clock fs-2 text-warning"></i>
                  </div>
                  <h4 className="text-warning">{tasksData.pending.length}</h4>
                  <p className="mb-0">Pending Tasks</p>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-success-transparent mb-3">
                    <i className="ti ti-check fs-2 text-success"></i>
                  </div>
                  <h4 className="text-success">{tasksData.completed.length}</h4>
                  <p className="mb-0">Completed</p>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body text-center">
                  <div className="avatar avatar-lg bg-info-transparent mb-3">
                    <i className="ti ti-trophy fs-2 text-info"></i>
                  </div>
                  <h4 className="text-info">
                    {
                      tasksData.completed.filter(
                        (t) =>
                          t.grade &&
                          (t.grade.includes("A") || t.grade.includes("B"))
                      ).length
                    }
                  </h4>
                  <p className="mb-0">Good Grades</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks Content */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <ul className="nav nav-tabs card-header-tabs">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "pending" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("pending")}
                      >
                        <i className="ti ti-clock me-2"></i>
                        Pending ({tasksData.pending.length})
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${
                          activeTab === "completed" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("completed")}
                      >
                        <i className="ti ti-check me-2"></i>
                        Completed ({tasksData.completed.length})
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="card-body">
                  {activeTab === "pending" && (
                    <div>
                      {tasksData.pending.length > 0 ? (
                        tasksData.pending.map((task) => renderTaskCard(task))
                      ) : (
                        <div className="text-center py-4">
                          <div className="avatar avatar-lg bg-success-transparent mb-3">
                            <i className="ti ti-check-check fs-2 text-success"></i>
                          </div>
                          <h5>All caught up!</h5>
                          <p className="text-muted">
                            No pending tasks. Great job!
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "completed" && (
                    <div>
                      {tasksData.completed.length > 0 ? (
                        tasksData.completed.map((task) =>
                          renderTaskCard(task, true)
                        )
                      ) : (
                        <div className="text-center py-4">
                          <div className="avatar avatar-lg bg-info-transparent mb-3">
                            <i className="ti ti-clipboard-list fs-2 text-info"></i>
                          </div>
                          <h5>No completed tasks</h5>
                          <p className="text-muted">
                            Completed tasks will appear here.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentTasks;
